import type { App } from 'vue'
import type { Router } from 'vue-router'
import type { Store } from 'vuex'
import { ANALYTICS_ENABLED, OPTOUT_KEY_PREFIX } from '@/constants/analytics'
import { createGtag } from './gtag'
import { createCore } from './core'
import type { Identity, PageCtx, OpToken } from './core'
import { routePattern, pageGroup, pageTitle, safeUrl, isExcluded, referrerOrigin } from './page'
import { normalizeApiError } from './api'
import { createTrackDirective } from './directive'
import { MENU_LABELS } from './labels'
import type { AnalyticsEventName, AnalyticsParams, SanitizeCtx } from './events'

// GA4 계측 플러그인 공개 API (설계 §9). 모든 메서드는 no-throw.
// 측정 ID 없음 / ANALYTICS_ENABLED=false / 하이버 아님 / 로그인 전 → 전부 no-op.
const MEASUREMENT_ID = String(process.env.VUE_APP_GA4_MEASUREMENT_ID || '')
const DEBUG = String(process.env.VUE_APP_GA4_DEBUG) === 'true'
const ENABLED = ANALYTICS_ENABLED && !!MEASUREMENT_ID && typeof window !== 'undefined'

const sanitizeCtx: SanitizeCtx = { routePatterns: new Set<string>(), labels: new Set<string>(MENU_LABELS) }

function safeStorage(kind: 'localStorage' | 'sessionStorage'): Storage | null {
  try {
    return window[kind]
  } catch (e) {
    return null
  }
}
function readLocal(key: string): string {
  try {
    const s = safeStorage('localStorage')
    return (s && s.getItem(key)) || ''
  } catch (e) {
    return ''
  }
}

const gtag = createGtag(MEASUREMENT_ID)
const core = ENABLED
  ? createCore({
      gtag,
      measurementId: MEASUREMENT_ID,
      debug: DEBUG,
      origin: window.location.origin,
      storage: safeStorage('localStorage'),
      sessionStorage: safeStorage('sessionStorage'),
      optOutPrefix: OPTOUT_KEY_PREFIX,
      sanitizeCtx,
    })
  : null

const reported = new WeakSet<object>() // 같은 요청 config 객체의 중복 보고 방지 (config 를 변경하지 않는다)
const requestOps = new WeakMap<object, OpToken>() // 요청 config → 요청 시작 시점 토큰 (늦은 오류의 계정 귀속 방지)
const NOOP_TOKEN: OpToken = { gen: -1, common: {} }
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let storeRef: Store<any> | null = null
let routerRef: Router | null = null
let currentUserId: string | null = null
let identityRev = 0 // sellerInfo/adminInfo 커밋마다 증가
let prev = { routePattern: '', safeUrl: '' }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function identityFrom(store: Store<any>): Identity | null {
  const s = store.state.sellerInfo || {}
  const a = store.state.adminInfo || {}
  const sellerNo = Number(s.sellerNo)
  if (!sellerNo) return null
  const isAdmin = Number(readLocal('viewType')) === 1 || Number(a.accountType) === 1
  if (isAdmin) {
    const adminNo = String(a.sellerNo || readLocal('no') || '')
    if (!adminNo) return null
    return {
      userId: `a${adminNo}`,
      accountType: 'admin',
      sellerStatus: null,
      scopeSellerNo: String(sellerNo),
      serviceNo: Number(s.serviceNo),
    }
  }
  return {
    userId: `s${sellerNo}`,
    accountType: 'seller',
    sellerStatus: s.sellerStatus ? String(s.sellerStatus) : null,
    scopeSellerNo: String(sellerNo),
    serviceNo: Number(s.serviceNo),
  }
}

// 재평가 — Vue watcher 콜백에서 직접 호출되므로 본문 전체가 no-throw 여야 한다
function evaluateNow() {
  try {
    if (!core || !storeRef) return
    const id = identityFrom(storeRef)
    if (id) currentUserId = id.userId
    core.evaluate({ hasToken: !!readLocal('accessToken'), identity: id, identityRev })
  } catch (e) {
    /* noop */
  }
}

export const analytics = {
  track(name: AnalyticsEventName, params?: AnalyticsParams) {
    try {
      if (core) core.track(name, params)
    } catch (e) {
      /* noop */
    }
  },
  trackOnce(name: AnalyticsEventName, params?: AnalyticsParams) {
    try {
      if (core) core.trackOnce(name, params)
    } catch (e) {
      /* noop */
    }
  },
  beginOp(): OpToken {
    try {
      return core ? core.beginOp() : NOOP_TOKEN
    } catch (e) {
      return NOOP_TOKEN
    }
  },
  endOp(token: OpToken, name: AnalyticsEventName, params?: AnalyticsParams) {
    try {
      if (core) core.endOp(token, name, params)
    } catch (e) {
      /* noop */
    }
  },
  beginAuth(method: 'password' | 'otp' | 'admin' | 'callback', expectedUserId: string) {
    try {
      if (core) core.beginAuth(method, expectedUserId)
    } catch (e) {
      /* noop */
    }
  },
  endAuth(reason: 'user' | 'session_expired' | 'auth_error') {
    try {
      if (core) core.endAuth(reason)
    } catch (e) {
      /* noop */
    }
  },
  sellerSwitch(targetSellerNo: string) {
    try {
      if (core) core.sellerSwitch(targetSellerNo)
    } catch (e) {
      /* noop */
    }
  },
  setOptOut(on: boolean) {
    try {
      if (core && currentUserId) {
        core.setOptOut(currentUserId, on)
        evaluateNow() // OFF 는 같은 탭에 storage 이벤트가 없으므로 즉시 재평가
      }
    } catch (e) {
      /* noop */
    }
  },
  isOptedOut(): boolean {
    try {
      return !!(core && currentUserId && core.isOptedOut(currentUserId))
    } catch (e) {
      return false
    }
  },
  // Axios 요청 인터셉터에서 호출 — config 객체를 키로 요청 시작 컨텍스트를 기억한다 (config 를 변경하지 않는다)
  markRequest(config: unknown) {
    try {
      if (core && config && typeof config === 'object') requestOps.set(config as object, core.beginOp())
    } catch (e) {
      /* noop */
    }
  },
  reportApiError(error: unknown) {
    try {
      if (!core) return
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = error as any
      let op: OpToken | undefined
      if (e && typeof e === 'object' && e.config && typeof e.config === 'object') {
        if (reported.has(e.config)) return
        reported.add(e.config)
        op = requestOps.get(e.config)
      }
      const n = normalizeApiError(error)
      if (n) core.reportApiErrorParams(n, op)
    } catch (e) {
      /* noop */
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  install(app: App, options: { router: Router; store: Store<any> }) {
    try {
      app.directive(
        'track',
        createTrackDirective((name, params) => analytics.track(name, params))
      )
      if (!core) return
      const { router, store } = options
      storeRef = store
      routerRef = router
      router.getRoutes().forEach((r) => sanitizeCtx.routePatterns.add(r.path))
      prev = { routePattern: '', safeUrl: referrerOrigin(document.referrer) }
      gtag.ensureStub()

      // 재평가 입력 감시 (설계 §9.2) — 객체 자체를 포함해 같은 값의 재커밋도 rev 를 올린다
      store.watch(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s: any) => [
          s.sellerInfo,
          s.adminInfo,
          s.sellerInfo?.sellerNo,
          s.sellerInfo?.serviceNo,
          s.sellerInfo?.sellerStatus,
          s.adminInfo?.sellerNo,
        ],
        () => {
          identityRev++
          evaluateNow()
        },
        { immediate: true }
      )
      // store.removeToken 은 localStorage 를 지우지 않으므로 mutation 을 직접 구독해 무효화 (Logout.vue 의 endAuth 뒤 중복 호출은 no-op)
      store.subscribe((mutation) => {
        try {
          if (mutation.type === 'removeToken') core.endAuth('auth_error')
        } catch (e) {
          /* noop */
        }
      })
      window.addEventListener('storage', (ev) => {
        try {
          core.onExternalStorage(ev.key)
          if (ev.key === null || ev.key === 'accessToken' || (ev.key || '').indexOf(OPTOUT_KEY_PREFIX) === 0) evaluateNow()
        } catch (e) {
          /* noop */
        }
      })
      router.afterEach((to, from, failure) => {
        try {
          if (failure) return
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const pattern = routePattern(to as any)
          if (isExcluded(pattern)) return
          if (pattern !== prev.routePattern) {
            // 쿼리만 바뀐 이동은 page_view 제외 (access_denied 는 아래에서 매번 기록)
            const ctx: PageCtx = {
              routePattern: pattern,
              pageGroup: pageGroup(pattern),
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              pageTitle: pageTitle(to as any, pattern),
              safeUrl: safeUrl(window.location.origin, pattern),
              prevRoutePattern: prev.routePattern,
              prevSafeUrl: prev.safeUrl,
            }
            prev = { routePattern: pattern, safeUrl: ctx.safeUrl }
            core.pageView(ctx)
          }
          if (pattern === '/not-authorized' && routerRef) {
            // 쿼리 원문이 아니라 라우터가 해석한 템플릿만 보낸다
            const fromPath = String((to.query && to.query.from) || '')
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const denied = fromPath ? routePattern(routerRef.resolve({ path: fromPath }) as any) : '/unknown'
            core.track('access_denied', { denied_route_pattern: denied })
          }
        } catch (e) {
          /* noop */
        }
      })
    } catch (e) {
      /* noop */
    }
  },
}

export { bucket, filterBucket } from './events'
export { resultOf, errorCodeOf } from './result'
export type { OpToken } from './core'

export default analytics
